import { HttpErrorResponse, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';

import {
  AuthService,
  AuthState,
  AuthenticationResponseModel,
  ForbiddenComponent,
  selectAccessToken,
  selectDsToken,
  selectRefreshToken,
  setAccessToken,
  setRefreshToken,
  setReloginUrl,
} from '@lib/auth';
import { Store } from '@ngrx/store';
import { Observable, catchError, finalize, forkJoin, shareReplay, switchMap, take, tap } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';

import { normalizeUrl } from '@lib/core/functions/normalize-url.function';

let refreshTokenRequest: Observable<AuthenticationResponseModel> | null = null;

export function AuthInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn) {
  const router = inject(Router);
  const authService = inject(AuthService);
  const store = inject(Store<AuthState>);
  const dialog = inject(MatDialog);

  const url = normalizeUrl(req.url);
  const whitelistedUrls = ['/assets'];
  const isWhitelisted = whitelistedUrls.some(whitelistedUrl => url?.pathname.startsWith(whitelistedUrl));

  if (isWhitelisted) {
    return next(req);
  }

  const handleRefreshError = (error: HttpErrorResponse): HttpErrorResponse => {
    // Save the current URL for redirect after login (unless already on login)
    if (router.url !== '/login') {
      store.dispatch(setReloginUrl({ reloginUrl: router.url }));
    }

    // Dispatch logout action and navigate to log in
    authService.logout();

    return error;
  };

  return forkJoin({
    dsAccessToken: store.select(selectDsToken).pipe(take(1)),
    accessToken: store.select(selectAccessToken).pipe(take(1)),
    refreshToken: store.select(selectRefreshToken).pipe(take(1)),
  }).pipe(
    switchMap(({ dsAccessToken, accessToken, refreshToken }) => {
      const token = dsAccessToken || accessToken;
      const authReq = token
        ? req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`),
          })
        : req;

      return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error && error.status === 401 && !authReq.url.includes('access/login/refresh')) {
            if (refreshToken) {
              if (!refreshTokenRequest) {
                refreshTokenRequest = authService.postRefreshToken(refreshToken).pipe(
                  tap((refreshResponse: AuthenticationResponseModel) => {
                    store.dispatch(setAccessToken({ accessToken: refreshResponse.accessToken }));
                    store.dispatch(setRefreshToken({ refreshToken: refreshResponse.refreshToken }));
                  }),
                  shareReplay(1),
                  finalize(() => {
                    refreshTokenRequest = null;
                  }),
                  catchError((pipeError: HttpErrorResponse) => {
                    throw handleRefreshError(pipeError);
                  })
                );
              }

              return refreshTokenRequest.pipe(
                switchMap((refreshResponse: AuthenticationResponseModel) => {
                  return next(
                    authReq.clone({
                      headers: authReq.headers.set('Authorization', `Bearer ${refreshResponse.accessToken}`),
                    })
                  );
                })
              );
            }
            throw handleRefreshError(error);
          }

          if (error && error.status === 403) {
            dialog.open(ForbiddenComponent, {
              width: '1200px',
              minWidth: '900px',
            });
          }

          throw error;
        })
      );
    })
  );
}
